import { Button } from '@servicetim/ui/components';
import { Download, Share, X } from 'lucide-react';
import { useBrand } from '@/features/branding/brand-provider';
import { type InstallPrompt, useInstallPrompt } from './use-install-prompt';

/** Dismissable "install this app" banner; the dismissal persists in localStorage. */
export function InstallHint() {
  const prompt = useInstallPrompt();
  return <InstallHintBanner prompt={prompt} />;
}

/** Presentational half (tested with a fake prompt). */
export function InstallHintBanner({ prompt }: { prompt: InstallPrompt }) {
  const brand = useBrand();
  const { state, dismiss } = prompt;
  if (state.kind === 'hidden') return null;
  return (
    <section
      aria-label="Install the app"
      data-testid="install-hint"
      className="flex items-center gap-3 border-brand-100 border-b bg-accent-soft px-4 py-2 text-accent text-sm"
    >
      {state.kind === 'prompt' ? (
        <Download className="size-4 shrink-0" aria-hidden="true" />
      ) : (
        <Share className="size-4 shrink-0" aria-hidden="true" />
      )}
      <p className="min-w-0 flex-1">
        {state.kind === 'prompt' ? (
          <>
            Install <strong className="font-semibold">{brand.portalName}</strong> for quick access
            from your home screen.
          </>
        ) : (
          <>
            Add <strong className="font-semibold">{brand.portalName}</strong> to your home screen:
            tap Share, then "Add to Home Screen".
          </>
        )}
      </p>
      {state.kind === 'prompt' ? (
        <Button size="sm" onClick={() => void state.install().then((ok) => ok && dismiss())}>
          Install
        </Button>
      ) : null}
      <Button variant="ghost" size="icon" aria-label="Dismiss install hint" onClick={dismiss}>
        <X />
      </Button>
    </section>
  );
}
